import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraFormField, SpecraHeading, SpecraInlineBanner, SpecraInput, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useAuth } from '../../../../contexts/authContext';
import { projectApiConnect } from '../../../../lib/api/projectApi.connect';
import { projectDataClient } from '../../../../lib/connect/clients';
import { PreservedContent, SourceVersion } from './AmendmentSourceContext';
// An issuance never adopts an interpretation or edits proposal content. Manual
// findings remain attributed source claims while interpretation is incomplete.
export function AmendmentImpactReview({ projectId }) {
    const { user } = useAuth();
    if (!user) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "Sign in to review amendments."
    });
    return /*#__PURE__*/ _jsx(ScopedAmendmentReview, {
        projectId: projectId,
        principal: `${user.tenant_id}:${user.id}`
    }, `${user.tenant_id}:${user.id}:${projectId}`);
}
function ScopedAmendmentReview({ projectId, principal }) {
    const saving = useRef(false);
    const save = async (action)=>{
        if (saving.current) return;
        saving.current = true;
        try {
            await action();
        } catch  {
        /* Mutation error is rendered and the form retained. */ } finally{
            saving.current = false;
        }
    };
    const queryClient = useQueryClient();
    const key = [
        'solicitationReview',
        projectId,
        principal
    ];
    const state = useQuery({
        queryKey: key,
        queryFn: ()=>projectDataClient.readSolicitationReview({
                projectId
            }),
        retry: false,
        gcTime: 0,
        refetchOnMount: 'always'
    });
    const files = useQuery({
        queryKey: [
            'projectFiles',
            projectId,
            'amendment',
            principal
        ],
        queryFn: ()=>projectApiConnect.listProjectFiles(projectId),
        enabled: state.isSuccess && state.isFetchedAfterMount,
        retry: false,
        gcTime: 0
    });
    const [source, setSource] = useState('');
    const [category, setCategory] = useState('requirements');
    const [change, setChange] = useState('changed');
    const [beforeText, setBefore] = useState('');
    const [currentText, setCurrent] = useState('');
    const [locator, setLocator] = useState('');
    const [rationale, setRationale] = useState('');
    const [assessments, setAssessments] = useState({});
    const [materialKind, setMaterialKind] = useState('attachment');
    const [materialId, setMaterialId] = useState('');
    const refresh = ()=>queryClient.invalidateQueries({
            queryKey: key
        });
    const versions = state.data?.state?.versions ?? [];
    const impacts = state.data?.state?.impacts ?? [];
    const reviews = state.data?.state?.reviews ?? [];
    const materials = state.data?.state?.availableMaterials ?? [];
    const latest = versions.at(-1);
    const [comparisonId, setComparisonId] = useState('');
    const comparison = versions.find((v)=>v.id === comparisonId) ?? latest;
    const previous = versions.find((v)=>v.id === comparison?.previousId);
    const comparedImpacts = impacts.filter((i)=>i.versionId === comparison?.id);
    const latestImpacts = impacts.filter((i)=>i.versionId === latest?.id);
    const issue = useMutation({
        mutationFn: ()=>projectDataClient.issueSolicitationVersion({
                projectId,
                attachmentId: source
            }),
        onSuccess: refresh
    });
    const capture = useMutation({
        mutationFn: ()=>projectDataClient.captureProposalReview({
                projectId,
                versionId: latest?.id,
                materialKind,
                materialId
            }),
        onSuccess: refresh
    });
    const record = useMutation({
        mutationFn: ()=>projectDataClient.recordSolicitationImpact({
                projectId,
                impact: {
                    versionId: latest?.id,
                    category,
                    change,
                    beforeText,
                    currentText,
                    sourceLocator: locator,
                    rationale,
                    materialAssessments: materials.flatMap((m)=>{
                        const assessment = assessments[`${m.kind}:${m.id}`];
                        return assessment && assessment !== 'unassessed' ? [
                            {
                                materialKind: m.kind,
                                materialId: m.id,
                                affected: assessment === 'affected'
                            }
                        ] : [];
                    })
                }
            }),
        onSuccess: async ()=>{
            await refresh();
            setBefore('');
            setCurrent('');
            setLocator('');
            setRationale('');
            setAssessments({});
        }
    });
    const error = issue.isError || capture.isError || record.isError;
    const t = Testids.proposalAmendment;
    const busy = issue.isPending || capture.isPending || record.isPending;
    return /*#__PURE__*/ _jsx(SpecraCard, {
        className: "mb-4",
        "data-testid": t.panel,
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    size: "sm",
                    children: "Amendment impact review"
                }),
                state.isLoading && /*#__PURE__*/ _jsx("output", {
                    children: "Loading amendment review…"
                }),
                (state.isError || files.isError) && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "danger",
                    children: [
                        "We could not load amendment review.",
                        ' ',
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>{
                                void state.refetch();
                                void files.refetch();
                            },
                            children: "Retry"
                        })
                    ]
                }),
                error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    children: "The action was not saved. Check your access and source selection, then retry. Your entered comparison remains here."
                }),
                busy && /*#__PURE__*/ _jsx("output", {
                    children: "Saving amendment review…"
                }),
                state.isSuccess && state.isFetchedAfterMount && !files.isError && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            size: "caption",
                            tone: "muted",
                            children: "Issue the original solicitation first, then each amendment in order. Issuance records the source immediately, regardless of processing. Adopted and submitted work is preserved."
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Source document",
                            children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                "data-testid": t.source,
                                value: source,
                                onChange: (e)=>setSource(e.target.value),
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "Select an uploaded source"
                                    }),
                                    (files.data?.files ?? []).map((f)=>/*#__PURE__*/ _jsx("option", {
                                            value: f.attachmentId,
                                            children: f.name
                                        }, f.attachmentId))
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": t.issue,
                            disabled: !source || busy,
                            onClick: ()=>void save(()=>issue.mutateAsync()),
                            children: versions.length ? 'Issue amendment' : 'Record original solicitation'
                        }),
                        latest ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                                    tone: "warning",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            "data-testid": t.pending,
                                            children: "Interpretation pending"
                                        }),
                                        ". This comparison is incomplete. Missing findings do not mean no impact. Review the sources and record grounded findings below."
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Compare issued version",
                                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                        "data-testid": t.comparisonVersion,
                                        value: comparison?.id ?? '',
                                        onChange: (e)=>setComparisonId(e.target.value),
                                        children: versions.map((v)=>/*#__PURE__*/ _jsxs("option", {
                                                value: v.id,
                                                children: [
                                                    "Version ",
                                                    v.versionNumber,
                                                    " · ",
                                                    v.sourceName
                                                ]
                                            }, v.id))
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid gap-4 md:grid-cols-2",
                                    children: [
                                        /*#__PURE__*/ _jsxs("section", {
                                            "data-testid": t.before,
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraHeading, {
                                                    size: "sm",
                                                    children: "Before"
                                                }),
                                                previous ? /*#__PURE__*/ _jsx(SourceVersion, {
                                                    projectId: projectId,
                                                    version: previous
                                                }, previous.id) : /*#__PURE__*/ _jsx(SpecraText, {
                                                    children: "No earlier issued version."
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("section", {
                                            "data-testid": t.current,
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraHeading, {
                                                    size: "sm",
                                                    children: "Current"
                                                }),
                                                /*#__PURE__*/ _jsx(SourceVersion, {
                                                    projectId: projectId,
                                                    version: comparison ?? latest
                                                }, (comparison ?? latest).id)
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("details", {
                                    children: [
                                        /*#__PURE__*/ _jsxs("summary", {
                                            children: [
                                                "Issued source history (",
                                                versions.length,
                                                ")"
                                            ]
                                        }),
                                        versions.map((v)=>/*#__PURE__*/ _jsx(SourceVersion, {
                                                projectId: projectId,
                                                version: v
                                            }, v.id))
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("section", {
                                    "data-testid": t.reviews,
                                    className: "space-y-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraHeading, {
                                            size: "sm",
                                            children: "Potentially affected work"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraText, {
                                            as: "p",
                                            children: "Assess each stored material against the latest source changes. Unassessed work is pending. These manual reviews preserve a rules basis; they do not submit work or replace submission approval."
                                        }),
                                        materials.map((m)=>{
                                            const recorded = latestImpacts.flatMap((i)=>i.materialAssessments).filter((a)=>a.materialKind === m.kind && a.materialId === m.id);
                                            const status = recorded.some((a)=>a.affected) ? 'Affected' : recorded.length ? 'Unaffected' : 'Pending assessment';
                                            return /*#__PURE__*/ _jsxs("div", {
                                                "data-testid": t.materialRow,
                                                className: "rounded border border-border p-3",
                                                children: [
                                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        children: [
                                                            m.title,
                                                            " ·",
                                                            ' ',
                                                            m.kind === 'bid_baseline' ? 'submitted bid baseline' : m.kind,
                                                            " · ",
                                                            status
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraFormField, {
                                                        label: `Assess ${m.title} (${m.kind})`,
                                                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                                            value: assessments[`${m.kind}:${m.id}`] ?? 'unassessed',
                                                            onChange: (e)=>setAssessments({
                                                                    ...assessments,
                                                                    [`${m.kind}:${m.id}`]: e.target.value
                                                                }),
                                                            children: [
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: "unassessed",
                                                                    children: "Unassessed"
                                                                }),
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: "affected",
                                                                    children: "Affected"
                                                                }),
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: "unaffected",
                                                                    children: "Unaffected"
                                                                })
                                                            ]
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraText, {
                                                        as: "p",
                                                        size: "caption",
                                                        children: "Saved with the source comparison and rationale below. An affected finding takes precedence."
                                                    })
                                                ]
                                            }, `${m.kind}:${m.id}`);
                                        }),
                                        reviews.map((r)=>/*#__PURE__*/ _jsxs("div", {
                                                className: "rounded border border-border p-3",
                                                children: [
                                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        children: [
                                                            r.title,
                                                            " · ",
                                                            r.materialKind,
                                                            " ·",
                                                            ' ',
                                                            r.needsReview ? r.staleReasons.every((reason)=>reason.startsWith('rules interpretation pending:')) ? 'Pending assessment' : 'Needs review' : 'Current approval'
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        size: "caption",
                                                        children: [
                                                            "Reviewed ",
                                                            new Date(r.reviewedAt).toLocaleString(),
                                                            " · solicitation version",
                                                            ' ',
                                                            versions.find((v)=>v.id === r.versionId)?.versionNumber ?? r.versionId,
                                                            " · by ",
                                                            r.reviewedBy
                                                        ]
                                                    }),
                                                    r.staleReasons.map((reason)=>/*#__PURE__*/ _jsx(SpecraText, {
                                                            as: "p",
                                                            size: "caption",
                                                            children: readableReason(reason, versions)
                                                        }, reason)),
                                                    /*#__PURE__*/ _jsxs("details", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("summary", {
                                                                children: "View earlier review"
                                                            }),
                                                            /*#__PURE__*/ _jsxs(SpecraText, {
                                                                as: "p",
                                                                children: [
                                                                    "Preserved manual review · ",
                                                                    r.title,
                                                                    " · content hash ",
                                                                    r.contentHash
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsx(PreservedContent, {
                                                                json: r.contentSnapshotJson
                                                            }),
                                                            versions.filter((v)=>v.id === r.versionId).map((v)=>/*#__PURE__*/ _jsx(SourceVersion, {
                                                                    projectId: projectId,
                                                                    version: v
                                                                }, v.id))
                                                        ]
                                                    })
                                                ]
                                            }, r.id)),
                                        !reviews.length && /*#__PURE__*/ _jsx(SpecraText, {
                                            children: "No material review snapshots recorded. Impact analysis is incomplete."
                                        }),
                                        /*#__PURE__*/ _jsxs("details", {
                                            children: [
                                                /*#__PURE__*/ _jsx("summary", {
                                                    children: "Record a material review snapshot"
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                    as: "p",
                                                    size: "caption",
                                                    children: "Freeze the current stored material and solicitation basis after reviewing it. This is an explicit manual material review, separate from submission approval."
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                                    label: "Material type",
                                                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                                        "data-testid": t.materialKind,
                                                        value: materialKind,
                                                        onChange: (e)=>{
                                                            setMaterialKind(e.target.value);
                                                            setMaterialId('');
                                                        },
                                                        children: [
                                                            'section',
                                                            'staffing',
                                                            'pricing',
                                                            'attachment',
                                                            'bid_baseline'
                                                        ].map((kind)=>/*#__PURE__*/ _jsx("option", {
                                                                children: kind
                                                            }, kind))
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                                    label: "Material",
                                                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                                        "data-testid": t.material,
                                                        value: materialId,
                                                        onChange: (e)=>setMaterialId(e.target.value),
                                                        children: [
                                                            /*#__PURE__*/ _jsx("option", {
                                                                value: "",
                                                                children: "Choose material to review"
                                                            }),
                                                            materials.filter((m)=>m.kind === materialKind).map((m)=>/*#__PURE__*/ _jsx("option", {
                                                                    value: m.id,
                                                                    children: m.title
                                                                }, m.id))
                                                        ]
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    "data-testid": t.capture,
                                                    disabled: !materialId || busy,
                                                    onClick: ()=>void save(()=>capture.mutateAsync()),
                                                    children: "Record review snapshot"
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                latest?.previousId && /*#__PURE__*/ _jsxs("section", {
                                    className: "space-y-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraHeading, {
                                            size: "sm",
                                            children: "Record a source-backed comparison"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "Category",
                                            children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                                "data-testid": t.category,
                                                value: category,
                                                onChange: (e)=>setCategory(e.target.value),
                                                children: [
                                                    'requirements',
                                                    'dates',
                                                    'instructions',
                                                    'templates'
                                                ].map((c)=>/*#__PURE__*/ _jsx("option", {
                                                        children: c
                                                    }, c))
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "Change",
                                            children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                                "data-testid": t.change,
                                                value: change,
                                                onChange: (e)=>setChange(e.target.value),
                                                children: [
                                                    'added',
                                                    'changed',
                                                    'removed'
                                                ].map((c)=>/*#__PURE__*/ _jsx("option", {
                                                        children: c
                                                    }, c))
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "Before wording",
                                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                                "data-testid": t.beforeText,
                                                value: beforeText,
                                                onChange: (e)=>setBefore(e.target.value)
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "Current wording",
                                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                                "data-testid": t.currentText,
                                                value: currentText,
                                                onChange: (e)=>setCurrent(e.target.value)
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "Source page / section",
                                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                                "data-testid": t.locator,
                                                value: locator,
                                                onChange: (e)=>setLocator(e.target.value)
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "Why work may be affected",
                                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                                "data-testid": t.rationale,
                                                value: rationale,
                                                onChange: (e)=>setRationale(e.target.value)
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            "data-testid": t.saveImpact,
                                            disabled: busy || !locator.trim() || !rationale.trim() || change !== 'added' && !beforeText.trim() || change !== 'removed' && !currentText.trim(),
                                            onClick: ()=>void save(()=>record.mutateAsync()),
                                            children: "Record impact for review"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("section", {
                                    "data-testid": t.impacts,
                                    className: "space-y-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraHeading, {
                                            size: "sm",
                                            children: "Source-backed findings"
                                        }),
                                        comparedImpacts.map((i)=>/*#__PURE__*/ _jsxs("div", {
                                                className: "rounded border border-border p-3",
                                                children: [
                                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        children: [
                                                            i.category,
                                                            " · ",
                                                            i.change
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        children: [
                                                            "Before: ",
                                                            i.beforeText || 'Added — no prior wording'
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        children: [
                                                            "Current: ",
                                                            i.currentText || 'Removed'
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        children: [
                                                            "Source: ",
                                                            i.sourceLocator,
                                                            " · ",
                                                            i.rationale
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        size: "caption",
                                                        children: [
                                                            "Human finding · Version",
                                                            ' ',
                                                            versions.find((v)=>v.id === i.versionId)?.versionNumber,
                                                            " · recorded by",
                                                            ' ',
                                                            i.recordedBy
                                                        ]
                                                    }),
                                                    i.materialAssessments.map((a)=>/*#__PURE__*/ _jsxs(SpecraText, {
                                                            as: "p",
                                                            size: "caption",
                                                            children: [
                                                                materials.find((m)=>m.kind === a.materialKind && m.id === a.materialId)?.title ?? a.materialId,
                                                                ": ",
                                                                a.affected ? 'Affected' : 'Unaffected',
                                                                " — ",
                                                                i.rationale
                                                            ]
                                                        }, `${a.materialKind}:${a.materialId}`))
                                                ]
                                            }, i.id)),
                                        !comparedImpacts.length && /*#__PURE__*/ _jsx(SpecraText, {
                                            children: "No grounded findings recorded yet. Interpretation pending."
                                        })
                                    ]
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(SpecraText, {
                            children: "No issued sources yet. Record the original to establish a persistent review basis."
                        })
                    ]
                })
            ]
        })
    });
}
function readableReason(reason, versions) {
    const [label, id] = reason.split(': ');
    const version = versions.find((v)=>v.id === id);
    const labels = {
        'rules interpretation pending': 'Pending human assessment for version',
        'rules changed': 'Human finding affects this material on version',
        'later rules finding': 'A later human finding requires another review',
        'issued source removed or changed': 'Issued source is unavailable or has changed on version',
        'material content changed': 'Stored material has changed since this review',
        'material removed': 'Stored material is no longer available'
    };
    return `${labels[label] ?? label}${version ? ` ${version.versionNumber}` : ''}`;
}
